import { cva } from 'class-variance-authority';

export const buttonVariants = cva(
  "cursor-pointer active:scale-95 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        /**
         * `border-primary-strong/40` is a *rim*, not an outline. A verde pyetro
         * fill clears only 1.33:1 against the canvas and 1.57:1 against a white
         * card, so the button's silhouette dissolves into the page even though
         * its label reads 10.72:1. Petrolio at 40% composites over the fill into
         * a darker green that clears 3.05:1 / 3.59:1 against canvas and card
         * while staying 2.29:1 against the fill itself, so the edge defines the
         * shape without drawing a hard black line around it. (The composited
         * value is quoted in manual §16; keeping the literal out of source is
         * what `scripts/check-brand-guardrails.mjs` asks for.)
         * Full-strength petrolio was tried (2026-08-24) and read as a boxed-in
         * button; 40% is the lowest alpha that still clears 3:1. See manual §16.
         */
        default:
          'border border-primary-strong/40 bg-primary text-primary-foreground hover:bg-primary-hover',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline:
          'border bg-background hover:bg-primary/10 hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary-strong underline-offset-4 hover:underline',
        /** Same rim rationale as `default` — see the comment there. */
        pill: 'rounded-pill border border-primary-strong/40 bg-primary text-primary-foreground font-medium hover:bg-primary-hover transition-all duration-200 ease-in-out hover:scale-[1.02] hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0.5',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-control gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-control px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
      /**
       * Label register. The manual (§16) sets CTA labels in Eurocat uppercase,
       * but this is deliberately opt-in rather than the default: Eurocat is a
       * dotted face, and applying it to every button — including the secondary
       * and ghost buttons packed into dense forms — costs more legibility than
       * it buys brand. Reserve `display` for the primary call to action on a
       * screen; the backoffice keeps `sans` throughout (audit §9.6).
       */
      labelVariant: {
        sans: '',
        display: 'font-display uppercase tracking-[0.08em]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      labelVariant: 'sans',
    },
  },
);
